import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Brain, Menu, X } from "lucide-react";

export default function Nav({ onDemoClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const anchorHref = (hash) => (onLanding ? hash : `/${hash}`);

  const links = [
    { label: "Product", href: anchorHref("#features"), type: "anchor" },
    { label: "Workflows", href: anchorHref("#workflows"), type: "anchor" },
    { label: "Channels", href: anchorHref("#omnichannel"), type: "anchor" },
    { label: "Demo", href: anchorHref("#demo"), type: "anchor" },
    { label: "Pricing", href: anchorHref("#pricing"), type: "anchor" },
    { label: "Blog", href: "/blogs", type: "route" },
  ];

  return (
    <header
      data-testid="landing-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0a0a10]/70 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <div className="w-8 h-8 rounded-lg bg-[#7C5CFF] flex items-center justify-center shadow-[0_0_20px_rgba(124,92,255,0.5)]">
            <Brain className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex flex-col leading-none">
            <span className="text-[15px] font-semibold tracking-tight">
              AI CRM
            </span>
            <span className="text-[10px] text-white/40 font-mono tracking-wider uppercase mt-0.5">
              customer interaction platform
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) =>
            l.type === "route" ? (
              <Link
                key={l.label}
                to={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm text-white/70 hover:text-white transition-colors"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.label}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm text-white/70 hover:text-white transition-colors"
              >
                {l.label}
              </a>
            )
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://app.crm.flingy.in/login"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-signin-btn"
            className="text-sm text-white/70 hover:text-white transition-colors"
          >
            Sign in
          </a>
          <button
            data-testid="nav-demo-btn"
            onClick={onDemoClick}
            className="btn-primary text-white text-sm font-medium px-5 py-2 rounded-full"
          >
            Request demo
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-white/80"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/5 bg-[#0a0a10]/95 backdrop-blur-xl px-6 py-4 flex flex-col gap-4">
          {links.map((l) =>
            l.type === "route" ? (
              <Link
                key={l.label}
                to={l.href}
                onClick={() => setOpen(false)}
                className="text-sm text-white/80"
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.label}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-sm text-white/80"
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            )
          )}
          <a
            href="https://app.crm.flingy.in/login"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="text-sm text-white/70"
            data-testid="nav-mobile-signin"
          >
            Sign in
          </a>
          <button
            data-testid="nav-mobile-demo-btn"
            onClick={() => {
              setOpen(false);
              onDemoClick();
            }}
            className="btn-primary text-white text-sm font-medium px-5 py-2.5 rounded-full"
          >
            Request demo
          </button>
        </div>
      )}
    </header>
  );
}
